/**
 * Compact star rating display. Five stars rendered with fractional fill.
 * Read-only by default; pass `interactive` for click-to-rate.
 */
export interface StarRatingProps {
  /** Average rating, 0 to 5. */
  value: number;
  /** Total number of ratings, optional, shown in muted parens after the stars. */
  count?: number;
  /** Star size in px. */
  size?: number;
  /** If set, fires when the user clicks a star (1 to 5). */
  onRate?: (n: 1 | 2 | 3 | 4 | 5) => void;
  /** Visual style: "compact" hides the count, "full" shows it. */
  variant?: "compact" | "full";
}

export function StarRating({
  value,
  count,
  size = 12,
  onRate,
  variant = "full",
}: StarRatingProps) {
  const filled = Math.max(0, Math.min(5, value));
  const interactive = !!onRate;
  return (
    <span className={"sr" + (interactive ? " sr-int" : "")}>
      <span className="sr-stars" role="img" aria-label={`${value.toFixed(2)} out of 5 stars`}>
        {[1, 2, 3, 4, 5].map((n) => {
          const fill = Math.max(0, Math.min(1, filled - (n - 1)));
          return (
            <button
              key={n}
              type="button"
              className="sr-star"
              tabIndex={interactive ? 0 : -1}
              disabled={!interactive}
              onClick={() => onRate?.(n as 1 | 2 | 3 | 4 | 5)}
              aria-label={interactive ? `Rate ${n} of 5` : undefined}
            >
              <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
                <defs>
                  <linearGradient id={`sr-grad-${n}-${value.toFixed(2)}`} x1="0" x2="1" y1="0" y2="0">
                    <stop offset={`${fill * 100}%`} stopColor="var(--at-accent)" />
                    <stop offset={`${fill * 100}%`} stopColor="rgba(255,255,255,0.12)" />
                  </linearGradient>
                </defs>
                <path
                  d="M12 2.6l2.74 6.27 6.83.66-5.16 4.6 1.55 6.7L12 17.5l-5.96 3.33 1.55-6.7L2.43 9.53l6.83-.66L12 2.6z"
                  fill={`url(#sr-grad-${n}-${value.toFixed(2)})`}
                  stroke="rgba(255,255,255,0.18)"
                  strokeWidth="1"
                />
              </svg>
            </button>
          );
        })}
      </span>
      <span className="sr-num">{filled.toFixed(2)}</span>
      {variant === "full" && typeof count === "number" && (
        <span className="sr-count">({count.toLocaleString()})</span>
      )}
      <style>{`
        .sr { display: inline-flex; align-items: center; gap: 6px;
          font-family: var(--at-font-mono); font-variant-numeric: tabular-nums; }
        .sr-stars { display: inline-flex; gap: 2px; }
        .sr-star { background: transparent; border: 0; padding: 0;
          line-height: 0; cursor: ${interactive ? "pointer" : "default"};
          color: inherit; }
        .sr-star:disabled { cursor: default; }
        .sr-int .sr-star:hover svg { transform: scale(1.18); }
        .sr-star svg { transition: transform var(--at-dur-fast) var(--at-ease); }
        .sr-num { font-size: 11px; color: var(--at-fg); font-weight: 600; }
        .sr-count { font-size: 11px; color: var(--at-muted); }
      `}</style>
    </span>
  );
}
